'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { cn } from '@/ui/utils/style'
import { cva } from 'class-variance-authority'
import { ChevronDownIcon, LucideIcon } from 'lucide-react'
import { FC, useState } from 'react'
import { SVGProps } from 'react'
import * as Collapsible from '@/ui/atoms/collapsible/Collapsible'
import { NestedLink, PageLinkType } from '../../types'
import { Badge, GradientBadge } from '@/ui/atoms/badge/Badge'
import { Typography } from '@/ui/atoms/typography/Typography'

interface NavLinkComponentProps {
  children: React.ReactNode
  selected?: boolean
  isPlaceholder?: boolean
  className?: string
  postfix?: React.ReactNode
  Icon?: LucideIcon | FC<SVGProps<SVGElement>>
}

export interface NavLinkProps extends NavLinkComponentProps {
  to: string
  onClick?: () => void
}

export interface NavButtonProps extends NavLinkComponentProps {
  onClick: () => void
}

export interface ExternalNavLinkProps extends NavLinkComponentProps {
  href: string
}

export interface PlaceholderNavLinkProps extends NavLinkComponentProps {}

export function NavLink({ to, children, onClick, className, ...rest }: NavLinkProps) {
  const pathname = usePathname() ?? ''
  const firstPathSegment = pathname.split('/')[1]
  const linkPathSegment = to.split('/')[1]
  const selected = firstPathSegment === linkPathSegment

  return (
    <Link href={to} onClick={onClick} className={cn(focusVariants(), className)}>
      <NavLinkComponent selected={selected} {...rest}>
        {children}
      </NavLinkComponent>
    </Link>
  )
}

export function ExternalNavLink({ href, children, className, ...rest }: ExternalNavLinkProps) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className={cn(focusVariants(), className)}>
      <NavLinkComponent selected={false} {...rest}>
        {children}
      </NavLinkComponent>
    </a>
  )
}

export function NavButton({ onClick, children, className, ...rest }: NavButtonProps) {
  return (
    <button onClick={onClick} className={cn(focusVariants(), className)}>
      <NavLinkComponent selected={false} {...rest}>
        {children}
      </NavLinkComponent>
    </button>
  )
}

export function PlaceholderNavLink({ children, className, ...rest }: PlaceholderNavLinkProps) {
  return (
    <NavLinkComponent
      {...rest}
      isPlaceholder
      className={cn('cursor-default', className)}
      postfix={<Badge className="mr-0 ml-auto">Soon</Badge>}
    >
      {children}
    </NavLinkComponent>
  )
}

export function NavLinkComponent({
  children,
  selected,
  postfix,
  className,
  isPlaceholder,
  Icon,
}: NavLinkComponentProps) {
  return (
    <span
      className={cn(
        'flex w-full flex-row items-center gap-4 p-3 xl:p-0',
        textVariants({ selected, isPlaceholder }),
        className,
      )}
    >
      {Icon && <Icon className="h-5 w-5" />}
      {children}
      {postfix}
    </span>
  )
}

export const textVariants = cva('font-medium text-lg transition-colors xl:text-sm', {
  variants: {
    selected: {
      true: 'text-foreground',
      false: 'text-link-inactive hover:text-foreground',
    },
    isPlaceholder: {
      true: 'text-link-inactive',
    },
  },
})

export const focusVariants = cva(
  'rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
  {
    variants: {},
  },
)

export function PageLink({ link, closeMobileMenu }: { link: PageLinkType; closeMobileMenu: () => void }) {
  switch (true) {
    case !link.to && !link.group:
      return (
        <PlaceholderNavLink key={link.label} Icon={link.icon}>
          {link.label}
        </PlaceholderNavLink>
      )
    case typeof link.to === 'string':
      return (
        <NavLink key={link.label} to={link.to} onClick={closeMobileMenu} Icon={link.icon}>
          {link.label}

          {link.isNew && (
            <GradientBadge>
              <Typography variant="prompt" gradient>
                New
              </Typography>
            </GradientBadge>
          )}
        </NavLink>
      )
    case Array.isArray(link.group):
      return <NestedLinks link={link} closeMobileMenu={closeMobileMenu} />
  }
}

export function NestedLinks({ link, closeMobileMenu }: { link: NestedLink; closeMobileMenu: () => void }) {
  const pathname = usePathname() ?? ''
  const firstPathSegment = pathname.split('/')[1]
  const linkPathSegment = link.group?.[0]?.to.split('/')[1]
  const selected = firstPathSegment === linkPathSegment

  const [open, setOpen] = useState(true)
  return (
    <Collapsible.Root className="group/nav w-full" open={open} onOpenChange={setOpen}>
      <Collapsible.Trigger asChild>
        <button className="group w-full">
          <NavLinkComponent
            selected={selected}
            Icon={link.icon}
            postfix={
              <span className="mr-0 ml-auto block text-link-inactive group-hover:text-foreground">
                <ChevronDownIcon className="h-4 w-4 transition-transform duration-200 group-data-[state=open]/nav:rotate-180" />
              </span>
            }
          >
            {link.label}
          </NavLinkComponent>
        </button>
      </Collapsible.Trigger>

      <Collapsible.Content
        className={cn('ml-[10px] flex flex-col gap-2 border-border-light border-l pl-8', open && 'pt-2')}
      >
        {link.group?.map(({ label, to }) => {
          const subLinkSelected = pathname === to
          return (
            <NavLink key={label} to={to} onClick={closeMobileMenu} selected={subLinkSelected}>
              {label}
            </NavLink>
          )
        })}
      </Collapsible.Content>
    </Collapsible.Root>
  )
}
